// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Candidate,
  CandidateChangeStatus,
  CandidateDelete,
  CandidateEdit,
  CandidateState,
  CandidateResponse,
  BulkRejectPayload,
} from "./model";
import { RequestState } from "@utils/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { CandidateCreate } from "./model";
import { CandidateStatus, SnackMessage } from "@config/constant";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { VacancyInterviews } from "@slices/interviewSlice/model";

export const addCandidate = createAsyncThunk(
  "candidate/addCandidate",
  async ({ payload }: { payload: CandidateCreate }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.candidates, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data === "Duplicate candidate application."
              ? SnackMessage.error.duplicateCandidate
              : SnackMessage.error.addCandidate;
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const editCandidate = createAsyncThunk(
  "candidate/editCandidate",
  async (
    {
      id,
      candidateId,
      payload,
    }: { id: number; candidateId: number; payload: CandidateEdit },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(
          `${AppConfig.serviceUrls.candidates}/${candidateId}/vacancies/${id}`,
          payload,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.editCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data ||
            SnackMessage.error.editCandidate;
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getCandidates = createAsyncThunk(
  "candidate/getCandidates",
  async (
    {
      status,
      searchString,
      limit,
      offset,
      isClosed,
    }: {
      status: string[] | null;
      searchString?: string;
      limit: number | null;
      offset: number | null;
      isClosed: boolean | null;
    },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.candidates, {
          params: {
            status: status?.join(","),
            limit,
            offset,
            isClosed,
            searchString,
          },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.getCandidates,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getCandidateHistory = createAsyncThunk(
  "candidate/getCandidateHistory",
  async (
    { email, limit, offset }: { email: string; limit: number; offset: number },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      const url = `${AppConfig.serviceUrls.candidates}/${email}/histories?limit=${limit}&offset=${offset}`;
      APIService.getInstance()
        .get(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.getCandidateHistory,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteCandidates = createAsyncThunk(
  "candidate/deleteCandidates",
  async ({ payload }: { payload: CandidateDelete }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .delete(AppConfig.serviceUrls.candidates, { data: payload })
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.deleteCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.deleteCandidate,
              type: "error",
            }),
          );
        });
    });
  },
);

export const modifyCandidateStatus = createAsyncThunk(
  "candidate/modifyCandidateStatus",
  async (
    { id, payload }: { id: number; payload: { status: string } },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.candidates}/${id}/updateStatus`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data || SnackMessage.error.updateCandidate;
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const bulkModifyCandidateStatus = createAsyncThunk(
  "candidate/bulkModifyCandidateStatus",
  async (
    { ids, payload }: { ids: number[]; payload: { status: string } },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.candidates}/updateStatus`, {
          ids,
          status: payload.status,
        })
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data || SnackMessage.error.updateCandidate;
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const hireCandidates = createAsyncThunk(
  "candidate/hireCandidates",
  async (
    { id, payload }: { id: number; payload: CandidateChangeStatus },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.candidates}/${id}/hire`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data || SnackMessage.error.updateCandidate;
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const rejectCandidates = createAsyncThunk(
  "candidate/rejectCandidates",
  async (
    {
      id,
      payload,
    }: {
      id: number;
      payload: CandidateChangeStatus & { sendEmail?: boolean };
    },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.candidates}/${id}/reject`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateCandidate,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateCandidate,
              type: "error",
            }),
          );
        });
    });
  },
);

export const bulkRejectCandidates = createAsyncThunk(
  "candidate/bulkRejectCandidates",
  async (
    { payload }: { payload: BulkRejectPayload & { sendEmail?: boolean } },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.candidates}/reject`, {
          candidateVacancyIds: payload.candidateVacancyIds,
          rejectionType: payload.rejectionType,
          additionalInfo: payload.additionalInfo,
          sendEmail: payload.sendEmail ?? true,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data ?? SnackMessage.error.updateCandidate;
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message:
                typeof errorMessage === "string"
                  ? errorMessage
                  : SnackMessage.error.updateCandidate,
              type: "error",
            }),
          );
        });
    });
  },
);

export const checkBulkRejectionStatus = createAsyncThunk(
  "candidate/checkBulkRejectionStatus",
  async (_, { dispatch }) => {
    return new Promise<{ state: string }>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.candidates}/reject/status`)
        .then((response) => {
          if (response.data?.value) {
            resolve({ state: response.data.value as string });
          } else {
            reject("Invalid bulk rejection status response");
            dispatch(
              enqueueSnackbarMessage({
                message: "Unable to read bulk rejection status",
                type: "error",
              }),
            );
          }
        })
        .catch((error) => {
          reject(error);

          const errorMessage =
            error.response?.data || "Unable to read bulk rejection status";

          dispatch(
            enqueueSnackbarMessage({
              message:
                typeof errorMessage === "string"
                  ? errorMessage
                  : "Unable to read bulk rejection status",
              type: "error",
            }),
          );
        });
    });
  },
);

export const getCandidatesByVacancy = createAsyncThunk(
  "candidate/getCandidatesByVacancy",
  async (
    {
      vacancyId,
      status,
      limit,
      offset,
      searchString,
    }: {
      vacancyId: number;
      status?: string[] | null;
      limit?: number | null;
      offset?: number | null;
      searchString?: string | null;
    },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.vacancies}/${vacancyId}/candidates`, {
          params: {
            status: status?.join(","),
            limit,
            offset,
            searchString,
          },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.getCandidates,
              type: "error",
            }),
          );
        });
    });
  },
);

export const recordResumeReview = createAsyncThunk(
  "candidate/recordResumeReview",
  async (
    {
      candidateVacancyId,
      viewerEmail,
    }: {
      candidateVacancyId: number;
      viewerEmail: string;
    },
    { dispatch },
  ) => {
    return new Promise<{
      candidateVacancyId: number;
      viewerEmail: string;
    }>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.candidates}/${candidateVacancyId}/resume-review`,
        )
        .then(() => {
          resolve({ candidateVacancyId, viewerEmail });
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateCandidate,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getReferralCandidatesByUser = createAsyncThunk(
  "candidate/getReferralCandidatesByUser",
  async (
    {
      limit,
      offset,
      status,
      searchString,
    }: {
      limit: number | null;
      offset: number | null;
      status?: string;
      searchString?: string;
    },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.candidates}/referrals`, {
          params: { limit, offset, status, searchString },
        })
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.getCandidates,
              type: "error",
            }),
          );
        });
    });
  },
);

const initialState: CandidateState = {
  candidateResponse: {
    candidates: [],
    totalCount: 0,
  },
  candidateHistoryResponse: {
    candidates: [],
    totalCount: 0,
  },
  state: RequestState.IDLE,
  updateState: RequestState.IDLE,
  candidateHistoryRequestState: RequestState.IDLE,
  bulkRejectState: RequestState.IDLE,
};

const candidateSlice = createSlice({
  name: "candidate",
  initialState,
  reducers: {
    updateCandidateStatus: (
      state,
      action: PayloadAction<{ id: number; status: string }>,
    ) => {
      const { id, status } = action.payload;
      const candidate = state.candidateResponse.candidates.find(
        (c) => c.id === id,
      );
      if (candidate) {
        candidate.status = status;
      }
    },
    updateCandidateInterviews: (
      state,
      action: PayloadAction<{
        id: number;
        additionalInterview: VacancyInterviews;
      }>,
    ) => {
      const { id, additionalInterview } = action.payload;
      const candidate = state.candidateResponse.candidates.map((c) => {
        if (c.vacancyId === id) {
          return {
            ...c,
            interview: [...(c.interview || []), additionalInterview],
          };
        }
        return c;
      });
      state.candidateResponse.candidates = candidate;
    },
    updateCandidateNumberOfInterviewsByVacancyId: (
      state,
      action: PayloadAction<{ id: number }>,
    ) => {
      const { id } = action.payload;
      const candidate = state.candidateResponse.candidates.map((c) => {
        if (c.vacancyId === id) {
          return {
            ...c,
            numberOfInterviews: c.numberOfInterviews + 1,
          };
        }
        return c;
      });
      state.candidateResponse.candidates = candidate;
    },
    updateCandidateNumberOfInterviewsByCandidateId: (
      state,
      action: PayloadAction<{ id: number }>,
    ) => {
      const { id } = action.payload;
      const candidate = state.candidateResponse.candidates.find(
        (c) => c.id === id,
      );
      if (candidate) {
        candidate.numberOfInterviews = candidate.numberOfInterviews + 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addCandidate.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      addCandidate.fulfilled,
      (state, action: PayloadAction<Candidate>) => {
        state.candidateResponse.candidates = [
          action.payload,
          ...state.candidateResponse.candidates,
        ];
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(addCandidate.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(getCandidates.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getCandidates.fulfilled,
      (state, action: PayloadAction<CandidateResponse>) => {
        state.candidateResponse.candidates = action.payload.candidates;
        state.candidateResponse.totalCount = action.payload.totalCount;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getCandidates.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getCandidateHistory.pending, (state) => {
      state.candidateHistoryRequestState = RequestState.LOADING;
    });
    builder.addCase(
      getCandidateHistory.fulfilled,
      (state, action: PayloadAction<CandidateResponse>) => {
        state.candidateHistoryResponse.candidates = action.payload.candidates;
        state.candidateHistoryResponse.totalCount = action.payload.totalCount;
        state.candidateHistoryRequestState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getCandidateHistory.rejected, (state) => {
      state.candidateHistoryRequestState = RequestState.FAILED;
    });

    builder.addCase(deleteCandidates.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      deleteCandidates.fulfilled,
      (state, action: PayloadAction<number[]>) => {
        const deleteCandidateIdArray = action.payload;
        const updatedCandidateList = state.candidateResponse.candidates.filter(
          (Candidate) => !deleteCandidateIdArray.includes(Candidate.id),
        );
        state.candidateResponse.candidates = updatedCandidateList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(deleteCandidates.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(editCandidate.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      editCandidate.fulfilled,
      (state, action: PayloadAction<Candidate>) => {
        const response = action.payload;
        const updatedCandidateList = state.candidateResponse.candidates.map(
          (candidate) => {
            if (response.id === candidate.id) {
              return response;
            }
            return candidate;
          },
        );
        state.candidateResponse.candidates = updatedCandidateList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(editCandidate.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(modifyCandidateStatus.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      modifyCandidateStatus.fulfilled,
      (state, action: PayloadAction<Candidate>) => {
        const response = action.payload;
        const updatedCandidateList = state.candidateResponse.candidates.map(
          (candidate) => {
            if (response.id === candidate.id) {
              return response;
            }
            return candidate;
          },
        );
        state.candidateResponse.candidates = updatedCandidateList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(modifyCandidateStatus.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(bulkModifyCandidateStatus.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(bulkModifyCandidateStatus.fulfilled, (state) => {
      state.updateState = RequestState.SUCCEEDED;
    });
    builder.addCase(bulkModifyCandidateStatus.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(hireCandidates.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      hireCandidates.fulfilled,
      (state, action: PayloadAction<Candidate>) => {
        const response = action.payload;
        const updatedCandidateList = state.candidateResponse.candidates.map(
          (candidate) => {
            if (response.id === candidate.id) {
              return { ...response, status: CandidateStatus.HIRED };
            }
            return candidate;
          },
        );
        state.candidateResponse.candidates = updatedCandidateList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(hireCandidates.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(rejectCandidates.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      rejectCandidates.fulfilled,
      (state, action: PayloadAction<Candidate>) => {
        const response = action.payload;
        const updatedCandidateList = state.candidateResponse.candidates.map(
          (candidate) => {
            if (response.id === candidate.id) {
              return { ...response, status: CandidateStatus.REJECTED };
            }
            return candidate;
          },
        );
        state.candidateResponse.candidates = updatedCandidateList;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(rejectCandidates.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(bulkRejectCandidates.pending, (state) => {
      state.bulkRejectState = RequestState.LOADING;
    });
    builder.addCase(bulkRejectCandidates.fulfilled, (state) => {
      state.bulkRejectState = RequestState.SUCCEEDED;
    });
    builder.addCase(bulkRejectCandidates.rejected, (state) => {
      state.bulkRejectState = RequestState.FAILED;
    });

    builder.addCase(checkBulkRejectionStatus.pending, (state) => {
      state.bulkRejectState = RequestState.LOADING;
    });
    builder.addCase(checkBulkRejectionStatus.fulfilled, (state) => {
      state.bulkRejectState = RequestState.SUCCEEDED;
    });
    builder.addCase(checkBulkRejectionStatus.rejected, (state) => {
      state.bulkRejectState = RequestState.FAILED;
    });

    builder.addCase(getCandidatesByVacancy.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getCandidatesByVacancy.fulfilled,
      (state, action: PayloadAction<CandidateResponse>) => {
        state.candidateResponse.candidates = action.payload.candidates;
        state.candidateResponse.totalCount = action.payload.totalCount;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getCandidatesByVacancy.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(recordResumeReview.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });

    builder.addCase(recordResumeReview.fulfilled, (state, action) => {
      const { candidateVacancyId, viewerEmail } = action.payload;
      const candidate = state.candidateResponse.candidates.find(
        (c) => c.id === candidateVacancyId,
      );
      if (candidate) {
        candidate.resumeReviewedBy = viewerEmail;
      }
      state.updateState = RequestState.SUCCEEDED;
    });

    builder.addCase(recordResumeReview.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(getReferralCandidatesByUser.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getReferralCandidatesByUser.fulfilled,
      (state, action: PayloadAction<CandidateResponse>) => {
        state.candidateResponse.candidates = action.payload.candidates;
        state.candidateResponse.totalCount = action.payload.totalCount;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getReferralCandidatesByUser.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
  },
});

export const {
  updateCandidateStatus,
  updateCandidateNumberOfInterviewsByVacancyId,
  updateCandidateNumberOfInterviewsByCandidateId,
  updateCandidateInterviews,
} = candidateSlice.actions;

export default candidateSlice.reducer;
